// WP13 Gate 4 — Weekly Experience. Authoritative v1.1 Weekly report rendered
// verbatim from GET /api/wp12/weekly (active package). Three distinct layers:
//   A. WP10 Weekly Executive Read (editorial thesis)
//   B. WP10 Story Signals (exactly 4, upstream selection + order preserved)
//   C. WP5 Pattern Weekly Framings (5 broader pattern updates, no new ranking)
// WP8 movement appears only as separately-labelled analytical context.
// The 31 claim references are internal methodology provenance (WP5/WP8/WP9
// records, no Evidence resolution) — kept in the data model, not customer UI.
import React from "react";
import { Link } from "react-router-dom";
import { useWp12 } from "./useWp12";
import InfoTip from "./InfoTip";
import { StateChip } from "./TopPatternsTable";
import { zoneColor } from "./LeadersChart";

const fval = (v) => (typeof v === "string" && v.trim() && v.trim() !== "None" ? v.trim() : null);
const list = (v) => (Array.isArray(v) ? v.filter((x) => fval(typeof x === "string" ? x : x?.watchFor || x?.implication)) : []);
const enumLabel = (v) => (typeof v === "string" ? v.replace(/_/g, " ").toLowerCase() : "—");

const ROLE_LABEL = { PRIMARY: "Primary story", CONTEXT: "Context", REGULATORY_WATCH: "Regulatory watch" };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const cutoffLabel = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
    return m ? `Through ${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : null;
};

const zoneLabelOf = (zones, key) => zones.find((z) => z.scopeKey === key)?.label || enumLabel(key);

// ── Story Signal card (Layer B — WP10 editorial selection, order preserved) ──
const StoryCard = ({ s, zones }) => {
    const color = zoneColor(zones.find((z) => z.scopeKey === s.marketZoneKey)?.presentationColorToken || s.marketZoneKey);
    const fr = s.framing || {};
    const sc = s.structuralContext || {};
    return (
        <article className="card-flat p-5 border-l-4" style={{ borderLeftColor: color }} data-testid={`wp13-weekly-story-${s.canonicalPatternId}`}>
            <div className="flex items-center gap-2 flex-wrap text-[9px] uppercase tracking-wider text-[hsl(var(--mute))]">
                <span className="font-semibold" style={{ color }}>{zoneLabelOf(zones, s.marketZoneKey)}</span>
                <span>· {s.useCaseLabel}</span>
                {s.storyRole && <span className="px-1.5 py-0.5 rounded-full border border-[hsl(var(--line))] normal-case tracking-normal">{ROLE_LABEL[s.storyRole] || enumLabel(s.storyRole)}</span>}
            </div>
            {fval(fr.headline) && <h3 className="text-[14px] font-semibold leading-snug mt-2" data-testid="wp13-story-headline">{fr.headline}</h3>}
            {fval(fr.summary) && <p className="text-[11.5px] leading-relaxed text-[hsl(var(--ink-soft))] mt-1.5">{fr.summary}</p>}
            {(fval(fr.whyItMatters) || list(fr.watchFor).length > 0) && (
                <details className="mt-2">
                    <summary className="text-[10px] text-[hsl(var(--accent-ink))] cursor-pointer select-none">More on this story</summary>
                    <div className="mt-1.5 space-y-2">
                        {fval(fr.whyItMatters) && (
                            <div>
                                <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">Why it matters</div>
                                <p className="text-[11.5px] leading-relaxed">{fr.whyItMatters}</p>
                            </div>
                        )}
                        {list(fr.watchFor).length > 0 && (
                            <div>
                                <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">Watch for</div>
                                <ul className="list-disc pl-4 space-y-1">
                                    {fr.watchFor.map((w, i) => <li key={i} className="text-[11.5px] leading-relaxed">{typeof w === "string" ? w : w?.watchFor}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                </details>
            )}
            {/* Secondary analytical context — published values only, visually quiet */}
            <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap mt-3 pt-2.5 border-t border-[hsl(var(--line))]">
                <Link to={`/market-v2/patterns/${encodeURIComponent(s.canonicalPatternId)}`}
                      className="text-[11px] font-medium hover:text-[hsl(var(--accent-ink))] hover:underline"
                      data-testid={`wp13-story-pattern-link-${s.canonicalPatternId}`}>
                    {s.displayTitle || s.patternLabel} →
                </Link>
                {s.weeklyMovement?.primaryMovementClass && (
                    <span className="inline-flex items-center gap-1.5 text-[9px] text-[hsl(var(--mute))]">
                        Weekly movement <StateChip value={s.weeklyMovement.primaryMovementClass} />
                    </span>
                )}
                {sc.h12TrendClass && (
                    <span className="inline-flex items-center gap-1.5 text-[9px] text-[hsl(var(--mute))]">
                        12w trend <StateChip value={sc.h12TrendClass} />
                    </span>
                )}
                {s.strength?.displayScoreExact != null && (
                    <span className="text-[9px] text-[hsl(var(--mute))]">Score <span className="num font-semibold text-[hsl(var(--ink))]">{s.strength.displayScoreExact.toFixed(1)}</span></span>
                )}
            </div>
        </article>
    );
};

// ── Pattern Update card (Layer C — WP5 framing, publisher order, no rank) ────
const UpdateCard = ({ f, zones, isStoryLinked }) => {
    const color = zoneColor(zones.find((z) => z.scopeKey === f.marketZone)?.presentationColorToken || f.marketZone);
    const deep = [
        ["Observation", fval(f.observation)],
        ["Interpretation", fval(f.interpretation)],
        ["Strategic implication", fval(f.strategicImplication)],
        ["Uncertainty", fval(f.uncertaintyStatement)],
    ].filter(([, v]) => v);
    const watchFor = (Array.isArray(f.watchFor) ? f.watchFor : []).filter((w) => fval(w?.watchFor || w));
    const stakeholders = (Array.isArray(f.stakeholderImplications) ? f.stakeholderImplications : []).filter((s) => fval(s?.implication));
    return (
        <article className="card-flat p-5 border-l-4" style={{ borderLeftColor: color }} data-testid={`wp13-weekly-update-${f.canonicalPatternId}`}>
            <div className="flex items-center gap-2 flex-wrap text-[9px] uppercase tracking-wider text-[hsl(var(--mute))]">
                <span className="font-semibold" style={{ color }}>{zoneLabelOf(zones, f.marketZone)}</span>
                {f.useCase && <span>· {f.useCase}</span>}
                {isStoryLinked && (
                    <span className="px-1.5 py-0.5 rounded-full border border-[hsl(var(--line))] normal-case tracking-normal"
                          data-testid={`wp13-update-featured-${f.canonicalPatternId}`}>
                        Featured in the Executive Read
                    </span>
                )}
            </div>
            <Link to={`/market-v2/patterns/${encodeURIComponent(f.canonicalPatternId)}`}
                  className="block text-[12px] font-medium mt-1.5 hover:text-[hsl(var(--accent-ink))] hover:underline"
                  data-testid={`wp13-update-pattern-link-${f.canonicalPatternId}`}>
                {f.canonicalPatternName}
            </Link>
            {fval(f.weeklyHeadline) && <h3 className="text-[13px] font-semibold leading-snug mt-1" data-testid="wp13-update-headline">{f.weeklyHeadline}</h3>}
            {fval(f.weeklySummary) && <p className="text-[11.5px] leading-relaxed text-[hsl(var(--ink-soft))] mt-1.5">{f.weeklySummary}</p>}
            {(deep.length > 0 || watchFor.length > 0 || stakeholders.length > 0) && (
                <details className="mt-2" data-testid={`wp13-update-more-${f.canonicalPatternId}`}>
                    <summary className="text-[10px] text-[hsl(var(--accent-ink))] cursor-pointer select-none">View full update</summary>
                    <div className="mt-1.5 space-y-2.5">
                        {deep.map(([label, text]) => (
                            <div key={label}>
                                <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">{label}</div>
                                <p className="text-[11.5px] leading-relaxed">{text}</p>
                            </div>
                        ))}
                        {watchFor.length > 0 && (
                            <div>
                                <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">Watch for</div>
                                <ul className="list-disc pl-4 space-y-1">
                                    {watchFor.map((w, i) => <li key={i} className="text-[11.5px] leading-relaxed">{fval(w?.watchFor || w)}</li>)}
                                </ul>
                            </div>
                        )}
                        {stakeholders.length > 0 && (
                            <div>
                                <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-0.5">Stakeholder implications</div>
                                <div className="space-y-1">
                                    {stakeholders.map((s, i) => (
                                        <div key={i} className="text-[11.5px] leading-relaxed">
                                            <span className="font-medium">{s.stakeholderType}</span>
                                            <span className="text-[hsl(var(--ink-soft))]"> — {s.implication}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </details>
            )}
        </article>
    );
};

const EditorialList = ({ title, items, testId }) => {
    if (!items?.length) return null;
    return (
        <section className="space-y-2" data-testid={testId}>
            <h2 className="text-sm font-semibold">{title}</h2>
            <ul className="card-flat p-5 space-y-2.5 list-disc pl-9">
                {items.map((x, i) => <li key={i} className="text-[12px] leading-relaxed">{x}</li>)}
            </ul>
        </section>
    );
};

const Weekly = () => {
    const { status, data, error } = useWp12("weekly");
    const marketRes = useWp12("market");
    const [query, setQuery] = React.useState("");
    const [zoneKey, setZoneKey] = React.useState("");

    if (status === "loading") return <div className="p-8 text-sm text-[hsl(var(--mute))]" data-testid="wp13-weekly-loading">Loading…</div>;
    if (status === "error" || !data?.weeklyExecutiveRead) {
        return <div className="p-8 text-sm" data-testid="wp13-weekly-error">The weekly report could not be loaded{error ? ` (${error})` : ""}.</div>;
    }

    const er = data.weeklyExecutiveRead;
    const stories = data.storySignals || [];
    const updates = data.patternWeeklyFramings || [];
    const zones = marketRes.data?.marketZones || [];
    const storyIds = new Set(stories.map((s) => s.canonicalPatternId));
    const week = er.reportingWeek || data.navigationContext?.reportingWeek;
    const period = cutoffLabel(er.dataCutoff);
    const fullReadParas = fval(er.fullRead) ? er.fullRead.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean) : [];

    // Discovery — presentation-only filtering of published items (AND semantics).
    // Never hides the Executive Read or the lower editorial sections; never reorders.
    const q = query.trim().toLowerCase();
    const filterActive = Boolean(q || zoneKey);
    const matches = (fields) => !q || fields.some((f) => typeof f === "string" && f.toLowerCase().includes(q));
    const shownStories = stories.filter((s) =>
        (!zoneKey || s.marketZoneKey === zoneKey) &&
        matches([s.framing?.headline, s.framing?.summary, s.displayTitle, s.patternLabel,
                 zoneLabelOf(zones, s.marketZoneKey), s.useCaseLabel]));
    const shownUpdates = updates.filter((f) =>
        (!zoneKey || f.marketZone === zoneKey) &&
        matches([f.canonicalPatternName, f.weeklyHeadline, f.weeklySummary,
                 zoneLabelOf(zones, f.marketZone), f.useCase]));
    const nothingMatches = filterActive && shownStories.length === 0 && shownUpdates.length === 0;
    const clearFilters = () => { setQuery(""); setZoneKey(""); };

    return (
        <div className="max-w-[1240px] mx-auto space-y-9" data-testid="wp13-weekly"
             data-counts={`er:1;stories:${stories.length};updates:${updates.length};refs:${(data.claimReferences || []).length}`}>
            {/* ── Report identity — authoritative published reporting week ────── */}
            <header data-testid="wp13-weekly-header">
                <div className="eyebrow inline-flex items-center gap-2">
                    Weekly <span className="num normal-case">· {week}</span>
                    {period && <span className="normal-case tracking-normal text-[hsl(var(--mute))]">· {period}</span>}
                </div>
                <h1 className="text-2xl font-semibold leading-snug mt-2 max-w-[50rem]" data-testid="wp13-weekly-headline">{er.headline}</h1>
                {fval(er.executiveSummary) && (
                    <p className="text-[12.5px] leading-relaxed text-[hsl(var(--ink-soft))] mt-3 max-w-[50rem]" data-testid="wp13-weekly-summary">{er.executiveSummary}</p>
                )}
                {fval(er.whyItMatters) && (
                    <div className="card-flat p-4 mt-4 max-w-[50rem] border-l-4 border-[hsl(var(--accent))]" data-testid="wp13-weekly-why">
                        <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1">Why it matters</div>
                        <p className="text-[11.5px] leading-relaxed">{er.whyItMatters}</p>
                    </div>
                )}
            </header>

            {/* ── Key takeaways ────────────────────────────────────────────────── */}
            {(er.keyTakeaways || []).length > 0 && (
                <section className="space-y-2" data-testid="wp13-weekly-takeaways">
                    <h2 className="text-sm font-semibold">Key takeaways</h2>
                    <ul className="card-flat p-5 space-y-2.5 list-disc pl-9">
                        {er.keyTakeaways.map((t, i) => <li key={i} className="text-[12px] leading-relaxed">{t}</li>)}
                    </ul>
                </section>
            )}

            {/* ── Discovery — presentation-only search/filter over published items */}
            <div className="card-flat p-3 flex items-center gap-2 flex-wrap" data-testid="wp13-weekly-discovery">
                <input value={query} onChange={(e) => setQuery(e.target.value)}
                       placeholder="Search stories and pattern updates…" data-testid="wp13-weekly-search"
                       className="text-[11px] px-2.5 py-1.5 rounded border border-[hsl(var(--line))] bg-[hsl(var(--surface))] w-64" />
                <select value={zoneKey} onChange={(e) => setZoneKey(e.target.value)} data-testid="wp13-weekly-zone-filter"
                        className="text-[11px] px-2 py-1.5 rounded border border-[hsl(var(--line))] bg-[hsl(var(--surface))] max-w-[240px]">
                    <option value="">All market zones</option>
                    {zones.map((z) => <option key={z.scopeKey} value={z.scopeKey}>{z.label}</option>)}
                </select>
                {filterActive && (
                    <>
                        <span className="text-[10px] text-[hsl(var(--mute))]" data-testid="wp13-weekly-filter-feedback">
                            {shownStories.length} story signal{shownStories.length === 1 ? "" : "s"} · {shownUpdates.length} pattern update{shownUpdates.length === 1 ? "" : "s"}
                        </span>
                        <button type="button" onClick={clearFilters} data-testid="wp13-weekly-clear-filters"
                                className="text-[10px] text-[hsl(var(--accent-ink))] hover:underline ml-auto">
                            Clear filters
                        </button>
                    </>
                )}
            </div>

            {nothingMatches ? (
                <div className="card-flat p-6 text-center" data-testid="wp13-weekly-empty">
                    <p className="text-[12px] text-[hsl(var(--ink-soft))]">No weekly stories or pattern updates match these filters.</p>
                    <button type="button" onClick={clearFilters} className="text-[10.5px] text-[hsl(var(--accent-ink))] hover:underline mt-1.5">Clear filters</button>
                </div>
            ) : (
            <>
            {/* ── Story signals — WP10 selection, upstream order ───────────────── */}
            {shownStories.length > 0 && (
            <section className="space-y-3" data-testid="wp13-weekly-stories">
                <h2 className="text-sm font-semibold inline-flex items-center gap-2">
                    Story signals
                    <InfoTip label="Story signals" testId="wp13-tip-stories">
                        The stories selected for this week's published Executive Read. Selection and order come from the upstream editorial product — they are not a score ranking.
                    </InfoTip>
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {shownStories.map((s) => <StoryCard key={s.weeklyStorySignalId || s.canonicalPatternId} s={s} zones={zones} />)}
                </div>
            </section>
            )}

            {/* ── Broader pattern updates — WP5 framings, publisher order ─────── */}
            {shownUpdates.length > 0 && (
            <section className="space-y-3" data-testid="wp13-weekly-updates">
                <h2 className="text-sm font-semibold inline-flex items-center gap-2">
                    Pattern updates
                    <InfoTip label="Pattern updates" testId="wp13-tip-updates">
                        Pattern-specific weekly updates published for this reporting week. These exist independently of the Executive Read story selection and are not ranked — updates also featured above carry a small label.
                    </InfoTip>
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {shownUpdates.map((f) => (
                        <UpdateCard key={f.weeklyFramingId || f.canonicalPatternId} f={f} zones={zones}
                                    isStoryLinked={storyIds.has(f.canonicalPatternId)} />
                    ))}
                </div>
            </section>
            )}
            </>
            )}

            {/* ── Deeper editorial read — always visible, never filtered ──────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <EditorialList title="Market structure implications" items={(er.marketStructureImplications || []).filter(fval)} testId="wp13-weekly-structure" />
                <EditorialList title="Regulatory watch" items={(er.regulatoryWatch || []).filter(fval)} testId="wp13-weekly-regwatch" />
            </div>
            <EditorialList title="What to watch" items={(er.watchItems || []).filter(fval)} testId="wp13-weekly-watch" />

            {fullReadParas.length > 0 && (
                <details className="card-flat" data-testid="wp13-weekly-fullread">
                    <summary className="px-5 py-3 text-sm font-semibold cursor-pointer select-none">Full weekly read</summary>
                    <div className="px-5 pb-5 pt-1 space-y-3 border-t border-[hsl(var(--line))] max-w-[50rem]">
                        {fullReadParas.map((p, i) => <p key={i} className="text-[12px] leading-relaxed">{p}</p>)}
                    </div>
                </details>
            )}
        </div>
    );
};

export default Weekly;
